// Step 1 of the Dhātu lab: fifteen common roots as picture tiles (the root's picture, the root, its meaning) and every other root
// in the "More roots" list. Arrow keys move between tiles in reading order and by rows; Enter or Space chooses.

import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { LabRoot } from '@/prakrit/components/grammar/labs/verb-data'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { DhatuPicture } from '@/prakrit/components/common/dhatu-picture'
import { RootCombobox } from '@/prakrit/components/grammar/dhatu/root-combobox'
import { StepLabel } from '@/prakrit/components/grammar/dhatu/dhatu-ui'
import { TILE_ROOTS } from '@/prakrit/components/grammar/dhatu/labels'

/** Columns of a wrapping grid: the tiles that share the first tile's row. */
const columnsOf = (items: HTMLElement[]): number => {
    const top = items[0]?.offsetTop ?? 0

    return Math.max(1, items.filter(el => el.offsetTop === top).length)
}

const tileKeyNav = (event: KeyboardEvent<HTMLElement>): void => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return

    const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[data-tile]'))
    const index = items.indexOf(document.activeElement as HTMLElement)

    if (items.length === 0 || index === -1) return

    event.preventDefault()

    const cols = columnsOf(items)
    let next = index

    if (event.key === 'ArrowRight') next = Math.min(items.length - 1, index + 1)
    else if (event.key === 'ArrowLeft') next = Math.max(0, index - 1)
    else if (event.key === 'ArrowDown') next = Math.min(items.length - 1, index + cols)
    else if (event.key === 'ArrowUp') next = Math.max(0, index - cols)
    else if (event.key === 'Home') next = 0
    else next = items.length - 1

    items[next]?.focus()
}

interface RootTilesProps {
    roots: LabRoot[]
    value: string
    onChange: (root: string) => void
    moreLabelId: string
}

export const RootTiles: FC<RootTilesProps> = ({ roots, value, onChange, moreLabelId }): ReactNode => {
    const tiles = useMemo(() => TILE_ROOTS
        .map(t => roots.find(r => r.root === t))
        .filter((r): r is LabRoot => Boolean(r)), [roots])

    const onTile = tiles.some(t => t.root === value)

    return (
        <div className='flex flex-col gap-4'>
            <div
                role='group'
                aria-label='आम धातुएँ · Common roots'
                onKeyDown={tileKeyNav}
                className='grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-3'
            >
                {tiles.map((r, i) => {
                    const active = r.root === value

                    return (
                        <button
                            key={r.root}
                            type='button'
                            data-tile=''
                            aria-pressed={active}
                            aria-label={`${r.root}: ${r.hi} · ${r.en}`}
                            tabIndex={active || (!onTile && i === 0) ? 0 : -1}
                            onClick={() => onChange(r.root)}
                            className={cn(
                                'group flex min-h-[4.25rem] flex-col items-center justify-start gap-0.5 rounded-2xl border p-1.5 pb-2 text-center transition-colors',
                                active
                                    ? 'border-foreground bg-foreground text-background shadow-sm'
                                    : 'border-border bg-background/60 hover:border-saffron/60 hover:bg-saffron/8'
                            )}
                        >
                            <DhatuPicture
                                root={r.root}
                                decorative
                                sizes='(min-width: 1024px) 110px, (min-width: 640px) 20vw, 30vw'
                                className={cn('mb-1 w-full rounded-xl', active ? 'ring-background/40' : 'ring-border')}
                                imageClassName='transition-transform duration-300 motion-safe:group-hover:scale-105'
                                fallback={(
                                    <span aria-hidden='true' className={cn('mb-1 flex aspect-square w-full items-center justify-center rounded-xl ring-1', active ? 'bg-background/10 ring-background/40' : 'bg-saffron/10 ring-border')}>
                                        <span lang='pra' className={cn('literary text-4xl', active ? 'text-background' : 'text-saffron-strong')}>{r.root}</span>
                                    </span>
                                )}
                            />
                            <span lang='pra' className={cn('font-prakrit text-xl font-bold leading-tight', active ? 'text-background' : 'text-prakrit')}>
                                {r.root}
                            </span>
                            <span lang='en' data-ex='en' className={cn('max-w-full truncate text-[0.6875rem] leading-tight', active ? 'text-background/75' : 'text-muted-foreground')}>
                                {r.en}
                            </span>
                            <span lang='hi' data-ex='hi' className={cn('hindi max-w-full truncate text-[0.6875rem] leading-tight', active ? 'text-background/75' : 'text-muted-foreground')}>
                                {r.hi}
                            </span>
                        </button>
                    )
                })}
            </div>

            <div className='flex flex-col gap-1.5'>
                <StepLabel id={moreLabelId} hi='और धातुएँ' en={`More roots (all ${roots.length})`} />
                <RootCombobox roots={roots} value={value} onChange={onChange} showValue={!onTile} />
            </div>
        </div>
    )
}
